import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { Menu } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

interface MainNavProps {
  currentPath: string;
}

const MainNav = ({ currentPath }: MainNavProps) => {
  const { user, signOut } = useAuth();
  
  // Update paths to include pricing
  const paths = [
    { name: 'Home', path: '/' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'Demo', path: '/demo' },
    { name: 'Installation', path: '/installation' }
  ];

  // Add admin paths if user has role
  if (user) {
    paths.push({ name: 'Account', path: '/account' });
  }

  return (
    <div className="border-b">
      <div className="container max-w-6xl mx-auto flex items-center justify-between py-4 px-4">
        <Link to="/" className="font-bold text-xl">
          Accesstopia
        </Link>
        
        <div className="hidden md:flex items-center space-x-6">
          {paths.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`text-sm font-medium transition-colors hover:text-foreground ${
                currentPath === item.path ? 'text-foreground' : 'text-muted-foreground'
              }`}
            >
              {item.name}
            </Link>
          ))}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={`https://avatar.vercel.sh/${user?.email}.png`} alt={user?.email || "Avatar"} />
                    <AvatarFallback>
                      {user?.email?.charAt(0).toUpperCase() || "A"}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut}>Sign Out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/auth">
              <Button size="sm">
                Sign In
              </Button>
            </Link>
          )}
        </div>

        <Sheet>
          <SheetTrigger asChild className="md:hidden">
            <Button variant="ghost" size="sm">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="sm:max-w-xs">
            <SheetHeader>
              <SheetTitle>Accesstopia</SheetTitle>
              <SheetDescription>
                Navigate through the app.
              </SheetDescription>
            </SheetHeader>
            <div className="grid gap-4 py-4">
              {paths.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block text-sm font-medium transition-colors hover:text-foreground ${
                    currentPath === item.path ? 'text-foreground' : 'text-muted-foreground'
                  } py-2`}
                >
                  {item.name}
                </Link>
              ))}
              {user ? (
                <Button variant="destructive" size="sm" onClick={signOut}>
                  Sign Out
                </Button>
              ) : (
                <Link to="/auth">
                  <Button size="sm">
                    Sign In
                  </Button>
                </Link>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
};

export default MainNav;
